/* eslint-disable no-promise-executor-return */
import React, { Dispatch, SetStateAction, useContext, useRef, useState } from "react";
import { Button } from "@mui/material";
import { SuccessAlertContext } from "App";
import { OrderCheck, OrderSupplierCsv, PlaceOrder } from "../types";
import OrderAskConfirmTable from "./OrderAskConfirmTable";
import getCsvOrderAskConfirmApi from "../api/getCsvOrderAskConfirmApi";
import CsvUploadModal from "./CsvUploadModal";
import getCsvOrderAskConfirmCheckApi from "../api/getCsvOrderAskConfirmCheckApi";
import uploadOrderAskCsv from "../api/uploadOrderAskCsv";
import OrderConfirmTable from "./OrderConfirmTable";
import getCsvPlaceOrderConfirmCheckApi from "../api/getCsvPlaceOrderConfirmCheckApi";
import getCsvPlaceOrderConfirmApi from "../api/getCsvPlaceOrderConfirmApi";
import uploadPlaceOrderCsv from "../api/uploadPlaceOrderCsv";
import OrderSupplierConfirmTable from "./OrderSupplierConfirmTable";
import uploadOrderSupplierCsv from "../api/uploadOrderSupplierCsv";
import getCsvOrderSupplierConfirmApi from "../api/getCsvOrderSupplierConfirmApi";
import getCsvOrderSupplierConfirmCheckApi from "../api/getCsvOrderSupplierConfirmCheckApi";

const limit = 10;

/**
 * 確認画面でのアクションボタンたち
 * @param onClose モーダルを閉じる
 * @param onCsvUpload アップロードする
 * @param disabled 非活性
 * @returns JSX.Element
 */
function ActionButtons(onClose: Function, onCsvUpload: Function, disabled: boolean) {
  return (
    <>
      <Button variant="custom-outlined" onClick={() => onClose()}>
        キャンセル
      </Button>
      <Button variant="contained" onClick={() => onCsvUpload()} disabled={disabled}>
        登録
      </Button>
    </>
  );
}

type Props = {
  refresh: Function; // 注文依頼を登録した後に読み込み直す
  isOpenOrderAskCsvUpload?: boolean; // 注文依頼
  isOpenPlaceOrderCsvUpload?: boolean; // 発注CSV
  isOpenOrderSupplierCsvUpload?: boolean; // 出荷CSV
  setIsOpenOrderAskCsvUpload?: Dispatch<SetStateAction<boolean>>;
  setIsOpenPlaceOrderCsvUpload?: Dispatch<SetStateAction<boolean>>;
  setIsOpenOrderSupplierCsvUpload?: Dispatch<SetStateAction<boolean>>;
};

/**
 * CSVアップロードボタン
 * @param root0 コンポーネントのプロップス
 * @returns JSX.Element
 */
export default function CsvModals({
  refresh,
  isOpenOrderAskCsvUpload = false,
  isOpenPlaceOrderCsvUpload = false,
  isOpenOrderSupplierCsvUpload = false,
  setIsOpenOrderAskCsvUpload,
  setIsOpenPlaceOrderCsvUpload,
  setIsOpenOrderSupplierCsvUpload,
}: Props) {
  const [isCheck, setIsCheck] = useState(false); // 確認画面かどうか
  const [file, setFile] = useState<File | null>(null);
  const [csvOrderCheckList, setCsvOrderCheckList] = useState<OrderCheck[]>([]); // 本部用、注文依頼更新CSV確認
  const [csvPlaceOrderCheckList, setCsvPlaceOrderCheckList] = useState<PlaceOrder[]>([]);
  const [csvOrderSupplierCheckList, setCsvOrderSupplierCheckList] = useState<OrderSupplierCsv[]>(
    [],
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isMoreLoading, setIsMoreLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const isFastLoading = useRef(false);
  const [processId, setProcessId] = useState("");
  const [initCheckScreen, setInitCheckScreen] = useState(false);
  const [total, setTotal] = useState(0);
  const [confirmError, setConfirmError] = useState("");

  const { showAlert } = useContext(SuccessAlertContext);

  const onClose = () => {
    if (isLoading) return;
    setTimeout(() => {
      if (setIsOpenOrderAskCsvUpload) setIsOpenOrderAskCsvUpload(false);
      if (setIsOpenPlaceOrderCsvUpload) setIsOpenPlaceOrderCsvUpload(false);
      if (setIsOpenOrderSupplierCsvUpload) setIsOpenOrderSupplierCsvUpload(false);
      setCsvOrderCheckList([]);
      setCsvPlaceOrderCheckList([]);
      setCsvOrderSupplierCheckList([]);
      setIsCheck(false);
      setFile(null);
      setOffset(0);
      setProcessId("");
      setInitCheckScreen(false);
      setTotal(0);
      setIsLoading(false);
      setConfirmError("");
      isFastLoading.current = false;
    }, 200);
  };

  // 注文依頼CSVアップロード
  const onCsvOrderAskUpload = async () => {
    if (file) {
      setIsLoading(true);
      try {
        // CSV反映
        await uploadOrderAskCsv(processId);
        onClose();
        showAlert("注文依頼を登録しました。");
        refresh();
      } catch (err) {
        console.error(err);
        showAlert("注文依頼csvアップロードに失敗しました。", "error");
      } finally {
        setIsLoading(false);
      }
    } else {
      showAlert("予期せぬエラーが発生しました。", "error");
    }
  };

  // 注文依頼確認画面へ
  const goOrderAskConfirm = async () => {
    if (file) {
      setIsLoading(true);
      try {
        const csvData = await getCsvOrderAskConfirmApi({
          limit,
          csv_file: file,
        });
        console.log('依頼CSVData', csvData);
        console.log('依頼CSVData.error', csvData.error);

        setCsvOrderCheckList(csvData.orders);
        setTotal(csvData.total);
        setProcessId(csvData.processId);
        setOffset(csvData.orders.length);
        setConfirmError(csvData.error ?? "");
        setIsCheck(true);
      } catch (err) {
        console.error(err);
        showAlert("注文依頼CSVの確認データ取得に失敗しました。", "error");
      } finally {
        setIsLoading(false);
      }
    }
  };

  // 注文依頼画面追加読み込み
  const getMoreCheckOrder = async () => {
    if (initCheckScreen && total > offset) {
      setIsMoreLoading(true);
      // isFastLoading.current = true;
      try {
        const csvData = await getCsvOrderAskConfirmCheckApi({
          process_id: processId,
          offset,
          limit,
        });
        setOffset((prev) => prev + csvData.length);
        setCsvOrderCheckList((prev) => [...prev, ...csvData]);
      } catch (err) {
        console.error(err);
        showAlert("注文依頼CSVの確認データ取得に失敗しました。", "error");
      } finally {
        isFastLoading.current = false;
        setIsMoreLoading(false);
      }
    } else {
      isFastLoading.current = false;
    }
  };

  // 発注csvアップロード
  const onCsvPlaceOrderUpload = async () => {
    if (file) {
      setIsLoading(true);
      try {
        // CSV反映
        await uploadPlaceOrderCsv(processId);
        onClose();
        showAlert("発注しました。");
        refresh();
      } catch (err) {
        console.error(err);
        showAlert("発注csvアップロードに失敗しました。", "error");
      } finally {
        setIsLoading(false);
      }
    } else {
      showAlert("予期せぬエラーが発生しました。", "error");
    }
  };

  // 発注確認画面へ
  const goPlaceOrderConfirm = async () => {
    if (file) {
      setIsLoading(true);
      try {
        const csvData = await getCsvPlaceOrderConfirmApi({
          limit,
          csv_file: file,
        });
        setCsvPlaceOrderCheckList(csvData.orders);
        setTotal(csvData.total);
        setProcessId(csvData.processId);
        setOffset(csvData.orders.length);
        setConfirmError(csvData.error ?? "");
        setIsCheck(true);
      } catch (err) {
        console.error(err);
        showAlert("発注CSVの確認データ取得に失敗しました。", "error");
      } finally {
        setIsLoading(false);
      }
    }
  };

  // 発注画面追加読み込み
  const getMoreCheckPlaceOrder = async () => {
    if (initCheckScreen && total > offset) {
      setIsMoreLoading(true);
      // isFastLoading.current = true;
      try {
        const csvData = await getCsvPlaceOrderConfirmCheckApi({
          process_id: processId,
          offset,
          limit,
        });
        setOffset((prev) => prev + csvData.length);
        setCsvPlaceOrderCheckList((prev) => [...prev, ...csvData]);
      } catch (err) {
        console.error(err);
        showAlert("発注CSVの確認データ取得に失敗しました。", "error");
      } finally {
        isFastLoading.current = false;
        setIsMoreLoading(false);
      }
    } else {
      isFastLoading.current = false;
    }
  };

  // 出荷csvアップロード確定
  const onCsvOrderSupplierUpload = async () => {
    if (file) {
      setIsLoading(true);
      try {
        // CSV反映
        await uploadOrderSupplierCsv(processId);
        onClose();
        showAlert("出荷しました。");
        refresh();
      } catch (err) {
        console.error(err);
        showAlert("出荷csvアップロードに失敗しました。", "error");
      } finally {
        setIsLoading(false);
      }
    } else {
      showAlert("予期せぬエラーが発生しました。", "error");
    }
  };

  // 出荷確認画面へ
  const goOrderSupplierConfirm = async () => {
    if (file) {
      setIsLoading(true);
      try {
        const csvData = await getCsvOrderSupplierConfirmApi({
          limit,
          csv_file: file,
        });
        setCsvOrderSupplierCheckList(csvData.orders);
        setTotal(csvData.total);
        setProcessId(csvData.processId);
        setOffset(csvData.orders.length);
        setConfirmError(csvData.error ?? "");
        setIsCheck(true);
      } catch (err) {
        console.error(err);
        showAlert("出荷CSVの確認データ取得に失敗しました。", "error");
      } finally {
        setIsLoading(false);
      }
    }
  };

  // 出荷画面追加読み込み
  const getMoreCheckOrderSupplier = async () => {
    if (initCheckScreen && total > offset) {
      setIsMoreLoading(true);
      try {
        const csvData = await getCsvOrderSupplierConfirmCheckApi({
          process_id: processId,
          offset,
          limit,
        });
        setOffset((prev) => prev + csvData.length);
        setCsvOrderSupplierCheckList((prev) => [...prev, ...csvData]);
      } catch (err) {
        console.error(err);
        showAlert("出荷CSVの確認データ取得に失敗しました。", "error");
      } finally {
        isFastLoading.current = false;
        setIsMoreLoading(false);
      }
    } else {
      isFastLoading.current = false;
    }
  };

  return (
    <>
      <CsvUploadModal
        isOpenCsvUpload={isOpenOrderAskCsvUpload}
        isCheck={isCheck}
        onClose={onClose}
        goConfirm={goOrderAskConfirm}
        file={file}
        title="注文依頼CSVアップロード"
        setFile={setFile}
        isLoading={isLoading}
      />
      <OrderAskConfirmTable
        open={isOpenOrderAskCsvUpload && isCheck}
        onClose={onClose}
        orderList={csvOrderCheckList}
        ActionButtons={ActionButtons(onClose, onCsvOrderAskUpload, isLoading || !!confirmError)}
        title="注文依頼CSVアップロード確認"
        content="以下の注文依頼を変更します。よろしいですか？"
        isFastLoading={isFastLoading}
        getMoreStockList={getMoreCheckOrder}
        setInit={setInitCheckScreen}
        isLoading={isLoading}
        isMoreLoading={isMoreLoading}
        error={confirmError}
        errorTitle="以下のCSVデータにエラーがあります。ご確認ください。"
      />
      <CsvUploadModal
        isOpenCsvUpload={isOpenPlaceOrderCsvUpload}
        isCheck={isCheck}
        onClose={onClose}
        goConfirm={goPlaceOrderConfirm}
        file={file}
        title="発注CSVアップロード"
        setFile={setFile}
        isLoading={isLoading}
      />
      <OrderConfirmTable
        open={isOpenPlaceOrderCsvUpload && isCheck}
        onClose={onClose}
        orderList={csvPlaceOrderCheckList}
        ActionButtons={ActionButtons(onClose, onCsvPlaceOrderUpload, isLoading || !!confirmError)}
        title="発注CSVアップロード確認"
        content="以下の発注します。よろしいですか？"
        isFastLoading={isFastLoading}
        getMoreStockList={getMoreCheckPlaceOrder}
        setInit={setInitCheckScreen}
        isLoading={isLoading}
        isMoreLoading={isMoreLoading}
        error={confirmError}
        errorTitle="以下のCSVデータにエラーがあります。ご確認ください。"
      />
      <CsvUploadModal
        isOpenCsvUpload={isOpenOrderSupplierCsvUpload}
        isCheck={isCheck}
        onClose={onClose}
        goConfirm={goOrderSupplierConfirm}
        file={file}
        title="出荷CSVアップロード"
        setFile={setFile}
        isLoading={isLoading}
      />
      <OrderSupplierConfirmTable
        open={isOpenOrderSupplierCsvUpload && isCheck}
        onClose={onClose}
        orderList={csvOrderSupplierCheckList}
        ActionButtons={ActionButtons(
          onClose,
          onCsvOrderSupplierUpload,
          isLoading || !!confirmError,
        )}
        title="出荷CSVアップロード確認"
        content="以下の受注を出荷します。よろしいですか？"
        isFastLoading={isFastLoading}
        getMoreStockList={getMoreCheckOrderSupplier}
        setInit={setInitCheckScreen}
        isLoading={isLoading}
        isMoreLoading={isMoreLoading}
        error={confirmError}
        errorTitle="以下のCSVデータにエラーがあります。ご確認ください。"
      />
    </>
  );
}
